import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import FilterAutocomplete from "components/FilterAutocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/MUI";
import axios from "axios";
import { withAuthContext } from "context/Auth";
import MDBadge from "components/MDBadge";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarGroup, Tab, Tabs } from "@mui/material";
import { AccessTimeFilled, Cancel, CheckCircle, ManageAccountsOutlined } from "@mui/icons-material";
import breakpoints from "assets/theme/base/breakpoints";
import AdminIcon from "assets/images/bg-profile.jpeg";
import ImageCloud from "../../link";
import moment from "moment";
import MDButton from "components/MDButton";

function daysPassed(startDate, endDate) {
	const start = new Date(startDate);
	const end = new Date(endDate);
	const diffInMs = end - start;
	const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
	return diffInDays;
}

/* --------------------
	 Extracted & memoized table component
	 -------------------- */
const ComplainTableComponent = React.memo(function ComplainTableComponent({
	data,
	error,
	tabValue,
	columns,
	setTabValue,
	navigate,
	isLoading,
	pagination,
	currentPage,
	itemsPerPage,
	handlePageChange,
	filters,
	filterOptions,
	onFilterChange,
	onClearFilters,
	isFilterLoading,
	filterError
}) {

	const [tabsOrientation, setTabsOrientation] = useState("horizontal");
	// Resize listener: register once
	useEffect(() => {
		function handleTabsOrientation() {
			setTabsOrientation((_) => (window.innerWidth < breakpoints.values.sm ? "vertical" : "horizontal"));
		}

		window.addEventListener("resize", handleTabsOrientation);
		// call once to set initial
		handleTabsOrientation();

		return () => {
			window.removeEventListener("resize", handleTabsOrientation);
		};
	}, []); // empty array -> attach only once

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	// pass only the props the memoized component needs.
	const onNewClick = () => navigate(`/ComplainCategorySelect/New`);

	const statusOptions = useMemo(() => filterOptions?.statuses || [], [filterOptions?.statuses]);
	const categoryOptions = useMemo(
		() => filterOptions?.categories || [],
		[filterOptions?.categories]
	);
	const adminOptions = useMemo(() => filterOptions?.admins || [], [filterOptions?.admins]);
	const propertyOptions = useMemo(
		() => filterOptions?.properties || [],
		[filterOptions?.properties]
	);

	const categoryLabel = useCallback((option) => option?.title || "Untitled", []);
	const adminLabel = useCallback((option) => option?.name || option?.email || "Unnamed", []);
	const propertyLabel = useCallback((option) => {
		const tower = option?.Tower || "";
		const flat = option?.FlatNo || "";
		const combined = `${tower} ${flat}`.trim();
		return combined || option?.description || option?._id || "Property";
	}, []);

	return (
		<Card>
			<MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
				<Grid container style={{ width: "100%" }}>
					<Grid item xs={6}>
						<MDTypography variant="h6" color="white">
							{tabValue} Complain
						</MDTypography>
					</Grid>
					<Grid item xs={6} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
						<MDBadge badgeContent="New" color="dark" variant="gradient" size="sm" onClick={onNewClick} />
					</Grid>
				</Grid>
			</MDBox>

			<MDBox px={2} py={2} mt={2} bgcolor="rgba(255,255,255,0.8)" borderRadius="md">
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="status"
							label="Status"
							placeholder="All statuses"
							value={filters.status}
							options={statusOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="category"
							label="Category"
							placeholder="All categories"
							value={filters.category}
							options={categoryOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
							getOptionLabel={categoryLabel}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="admin"
							label="Admin"
							placeholder="All admins"
							value={filters.admin}
							options={adminOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
							getOptionLabel={adminLabel}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FilterAutocomplete
							name="property"
							label="Property"
							placeholder="All properties"
							value={filters.property}
							options={propertyOptions}
							onChange={onFilterChange}
							loading={isFilterLoading}
							disabled={isFilterLoading}
							getOptionLabel={propertyLabel}
						/>
					</Grid>
					<Grid item xs={12} display="flex" justifyContent="flex-end">
						<MDButton
							variant="outlined"
							color="info"
							size="small"
							disabled={isFilterLoading}
							onClick={onClearFilters}
						>
							Clear filters
						</MDButton>
					</Grid>
				</Grid>
				{filterError && (
					<MDTypography variant="caption" color="error">
						{filterError}
					</MDTypography>
				)}
			</MDBox>

			<MDBox
				mx={2}
				mt={-3}
				py={3}
				px={2}
				variant="gradient"
				bgColor="info"
				borderRadius="lg"
				coloredShadow="info"
			>
				<Grid container style={{ width: "100%", alignItems: "flex-end", justifyContent: "flex-end" }}>
					<Grid item xs={12}>
						<Tabs orientation={tabsOrientation} value={tabValue} onChange={handleTabChange}>
							<Tab label="All" value={"All"} icon={<ManageAccountsOutlined fontSize="small" sx={{ mt: -0.25 }} />} />
							<Tab label="Active" value={"Active"} icon={<AccessTimeFilled fontSize="small" sx={{ mt: -0.25 }} />} />
							<Tab label="Completed" value={"Completed"} icon={<CheckCircle fontSize="small" sx={{ mt: -0.25 }} />} />
							<Tab label="Canceled" value={"Canceled"} icon={<Cancel fontSize="small" sx={{ mt: -0.25 }} />} />
						</Tabs>
					</Grid>
				</Grid>
			</MDBox>
			<MDBox pt={3}>
				{isLoading ? (
					<MDBox lineHeight={1} textAlign="center" py={3}>
						<MDTypography display="block" variant="caption" color="text" fontWeight="medium" fontSize="16px">
							Loading...
						</MDTypography>
					</MDBox>
				) : data && data.length > 0 ? (
					<DataTable 
						rows={data} 
						columns={columns} 
						paginationMode="server"
						serverPagination={{
							total: pagination?.total || 0,
							page: currentPage,
							limit: itemsPerPage,
							onPageChange: handlePageChange
						}}
					/>
				) : (
					<MDBox lineHeight={1} textAlign="center">
						<MDTypography display="block" variant="caption" color="text" fontWeight="medium" fontSize="20px">
							{error ? error : " No Data Found"}
						</MDTypography>
					</MDBox>
				)}
			</MDBox>
		</Card>
	);
});

/* --------------------
	 Main component
	 -------------------- */
function Tables({ Token, CheckToken, TableOnly }) {
	const navigate = useNavigate();

	const [data, setData] = useState([]);
	const [error, setError] = useState("");
	const [tabValue, setTabValue] = useState("All");
	const [isLoading, setIsLoading] = useState(false);
	const isFetchingRef = useRef(false);
	const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [filters, setFilters] = useState({ status: "", category: "", admin: "", property: "" });
	const [filterOptions, setFilterOptions] = useState({ statuses: [], categories: [], admins: [], properties: [] });
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");

	// memoize columns so their reference doesn't change on every render
	const columns = useMemo(() => {
		return [
			{
				flex: 1,
				headerName: "status",
				field: "status",
				renderCell: (params) => (
					<MDBadge
						badgeContent={params?.row?.status}
						color={params?.row?.bgColor || "dark"}
						variant="gradient"
						size="sm"
					/>
				)
			},
			{ flex: 1, headerName: "Number", field: "Number" },
			{ flex: 1, headerName: "title", field: "title" },
			{ flex: 1, headerName: "description", field: "description" },
			{
				headerName: "Created At",
				field: "Created At",
				flex: 2,
				valueGetter: ({ row: a }) => `${moment(a?.created_at).format("DD-MM-YYYY hh:mm") || "-"}`
			},
			{
				headerName: "Time Passed",
				field: "Time Passed",
				flex: 2,
				valueGetter: ({ row: a }) => `${Math.round(daysPassed(a?.created_at, new Date())) + " days" || "-"}`
			},
			{
				flex: 1,
				headerName: "ComplainCategory",
				field: "ComplainCategory",
				valueGetter: (params) => `${params.row.ComplainCategory?.title || "-"}`
			},
			{
				headerName: "Tower No",
				field: "Tower No",
				flex: 1,
				valueGetter: ({ row: a }) => `${a?.Property?.Tower || "-"}`
			},
			{
				flex: 1,
				headerName: "Property",
				field: "Property",
				valueGetter: (params) => `${params.row.Property?.FlatNo || "-"}`
			},
			{
				flex: 1,
				headerName: "Assigned To",
				field: "AssignedTo",
				renderCell: (params) => (
					<div>
						{params?.row?.Tasks?.length > 0 ? (
							<AvatarGroup max={4}>
								{params?.row?.Tasks?.map((a) => (
									<Avatar
										key={a?.assignedTo?._id || Math.random()}
										alt="Assigned"
										src={
											a?.assignedTo?.profilePicture?.filename
												? `${ImageCloud}/${a?.assignedTo?.profilePicture?.filename}`
												: AdminIcon
										}
										sx={{ width: 30, height: 30 }}
									/>
								))}
							</AvatarGroup>
						) : (
							"-"
						)}
					</div>
				)
			},
			{
				flex: 1,
				headerName: "Action",
				field: "action",
				renderCell: (params) => (
					<MDBadge
						badgeContent={"Edit"}
						color={"warning"}
						variant="gradient"
						size="sm"
						onClick={() =>
							navigate(
								`/ComplainAction/${params.row?._id}/${params.row?.ComplainCategory?._id}/${params.row?.Property?._id}`
							)
						}
					/>
				)
			}
		];
		// include navigate if you want to be explicit. navigate is stable from react-router, so empty array is fine too
	}, [navigate]);

	// stabilize GetComplains using useCallback (optional but cleaner)
	const GetComplains = useCallback(() => {
		// Prevent duplicate API calls
		if (isFetchingRef.current) return;

		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");

		if (tokenHeader) {
			isFetchingRef.current = true;
			setIsLoading(true);
			const params = {
				page: currentPage,
				limit: itemsPerPage
			};
			if (filters.status) params.status = filters.status;
			if (filters.category) params.category = filters.category;
			if (filters.admin) params.admin = filters.admin;
			if (filters.property) params.property = filters.property;
			axios
				.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetAllComplain`, {
					params,
					headers: { Authorization: `Bearer ${tokenHeader}` }
				})
				.then((res) => {
					if (res?.data?.status === 200) {
						const arr = res?.data?.data
							.filter((a) => {
								const Time = (new Date() - new Date(a?.created_at)) / (1000 * 60 * 60);

								if (Time >= 72) {
									a.bgColor = "error";
								} else if (Time >= 48) {
									a.bgColor = "warning";
								} else if (Time >= 24) {
									a.bgColor = "secondary";
								} else {
									a.bgColor = "primary";
								}

								if (tabValue === "All") {
									return true;
								} else if (
									tabValue === "Active" &&
									(a?.status === "active" || a?.status === "inactive" || a?.status === "delayed")
								) {
									return true;
								} else if (tabValue === "Completed" && a?.status === "completed") {
									a.bgColor = "success";
									return true;
								} else if (
									tabValue === "Canceled" &&
									(a?.status === "canceled" || a?.status === "canceledByUser")
								) {
									a.bgColor = "error";
									return true;
								}
								return false;
							})
						.sort((a, b) => {
							const timeA = (new Date() - new Date(a?.created_at)) / (1000 * 60 * 60);
							const timeB = (new Date() - new Date(b?.created_at)) / (1000 * 60 * 60);
							if (timeA >= 72 && timeB >= 72) return timeA - timeB;
							if (timeA >= 48 && timeB >= 48) return timeA - timeB;
							if (timeA >= 24 && timeB >= 24) return timeA - timeB;
							return 0;
						});

					setData(arr);
					if (res?.data?.pagination) {
						setPagination(res?.data?.pagination);
					}
				} else {
					setError(res?.data?.message);
				}
				})
				.catch((err) => setError(err?.message))
				.finally(() => {
					setIsLoading(false);
					isFetchingRef.current = false;
				});
		} else {
			setTimeout(() => {
				CheckToken();
			}, 500);
		}
	}, [Token, tabValue, CheckToken, currentPage, itemsPerPage, filters]); // include deps used inside

	const handleSetTabValue = useCallback((newValue) => {
		setTabValue(newValue);
	}, []);

	const handlePageChange = useCallback((newPage, newPageSize) => {
		if (newPageSize !== itemsPerPage) {
			setItemsPerPage(newPageSize);
			setCurrentPage(1);
		} else {
			setCurrentPage(newPage);
		}
	}, [itemsPerPage]);

	const handleFilterChange = useCallback((field, value) => {
		setCurrentPage(1);
		setFilters((prev) => ({
			...prev,
			[field]: value || ""
		}));
	}, []);

	const handleClearFilters = useCallback(() => {
		setCurrentPage(1);
		setFilters({ status: "", category: "", admin: "", property: "" });
	}, []);

	const fetchComplainFilters = useCallback(() => {
		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");
		if (!tokenHeader) return;
		setIsFilterLoading(true);
		setFilterError("");
		axios
			.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetComplainFilters`, {
				headers: { Authorization: `Bearer ${tokenHeader}` }
			})
			.then((res) => {
				if (res?.data?.status === 200) {
					setFilterOptions(res?.data?.data || {});
				} else {
					setFilterError(res?.data?.message || "Unable to load filters");
				}
			})
			.catch((err) => {
				setFilterError(err?.response?.data?.message || err?.message || "Unable to load filters");
			})
			.finally(() => setIsFilterLoading(false));
	}, [Token]);

	useEffect(() => {
		fetchComplainFilters();
	}, [fetchComplainFilters]);

	// run GetComplains when component mounts and when dependencies change
	useEffect(() => {
		GetComplains();
	}, [GetComplains]);

	if (TableOnly) {
		return (
			<ComplainTableComponent
				data={data}
				error={error}
				tabValue={tabValue}
				navigate={navigate}
				columns={columns}
				setTabValue={handleSetTabValue}
				isLoading={isLoading}
				pagination={pagination}
				currentPage={currentPage}
				itemsPerPage={itemsPerPage}
				handlePageChange={handlePageChange}
				filters={filters}
				filterOptions={filterOptions}
				onFilterChange={handleFilterChange}
				onClearFilters={handleClearFilters}
				isFilterLoading={isFilterLoading}
				filterError={filterError}
			/>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox pt={6} pb={3}>
				<Grid container spacing={6}>
					<Grid item xs={12}>

					<MDBox pt={3}>
						<ComplainTableComponent
							data={data}
							error={error}
							tabValue={tabValue}
							navigate={navigate}
							columns={columns}
							setTabValue={handleSetTabValue}
							isLoading={isLoading}
							pagination={pagination}
							currentPage={currentPage}
							itemsPerPage={itemsPerPage}
							handlePageChange={handlePageChange}
							filters={filters}
							filterOptions={filterOptions}
							onFilterChange={handleFilterChange}
							onClearFilters={handleClearFilters}
							isFilterLoading={isFilterLoading}
							filterError={filterError}
						/>
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
		</DashboardLayout>
	);
}

export default withAuthContext(Tables);
